import {
    AUTOMATIC_TAX_CALCULATION,
    IOrderSale,
    SUBTOTAL,
    TAX,
    TOTAL_WITH_TAX,
} from '../order-sale-form';
import { useFormContext, UseFormReturn } from 'react-hook-form';
import { useOrderSaleOrderSaleReceiptTypeInputQuery } from '../../../../../../../services';

const PRODUCT_TOTAL_FIELD_NAME: keyof IOrderSale['order_sale_products'][number] =
    'total';
const ORDER_SALE_PRODUCTS_FIELD_NAME: keyof IOrderSale = 'order_sale_products';

function setProductsTotal({
    orderSaleProducts,
    taxMultiplier,
    automaticTaxCalculation,
    formContext,
    index,
}: {
    orderSaleProducts: IOrderSale['order_sale_products'];
    taxMultiplier: number;
    automaticTaxCalculation: IOrderSale['automatic_tax_calculation'];
    formContext: UseFormReturn<any>;
    index?: number;
}) {
    if (orderSaleProducts && Array.isArray(orderSaleProducts)) {
        const productsTotal = (
            orderSaleProducts as IOrderSale['order_sale_products']
        ).reduce((acc, orderSaleProduct, currentIndex) => {
            const kilos =
                Number.isNaN(orderSaleProduct.kilos) || !orderSaleProduct.kilos
                    ? 0
                    : orderSaleProduct.kilos;
            const kiloPrice =
                Number.isNaN(orderSaleProduct.kilo_price) ||
                !orderSaleProduct.kilo_price
                    ? 0
                    : orderSaleProduct.kilo_price;

            const groupPrice =
                Number.isNaN(orderSaleProduct.group_price) ||
                !orderSaleProduct.group_price
                    ? 0
                    : orderSaleProduct.group_price;

            const groups =
                Number.isNaN(orderSaleProduct.groups) ||
                !orderSaleProduct.groups
                    ? 0
                    : orderSaleProduct.groups;

            const kiloTotal = kilos * kiloPrice;

            const groupsTotal = groupPrice * groups;

            const productTotal = kiloTotal + groupsTotal;

            if (index !== undefined && index === currentIndex) {
                formContext.setValue(
                    `${ORDER_SALE_PRODUCTS_FIELD_NAME}.${index}.${PRODUCT_TOTAL_FIELD_NAME}`,
                    Math.round(productTotal * 100) / 100,
                    {
                        shouldValidate: true,
                    },
                );
            }

            return acc + productTotal;
        }, 0);

        formContext.setValue(SUBTOTAL, Math.round(productsTotal * 100) / 100, {
            shouldValidate: true,
        });

        const appliesTax = taxMultiplier > 1;
        const currentTax = Number(formContext.getValues(TAX));
        const manualTax = Number.isFinite(currentTax) ? currentTax : 0;
        const tax = !appliesTax
            ? 0
            : automaticTaxCalculation
            ? productsTotal * taxMultiplier - productsTotal
            : manualTax;

        if (!appliesTax || automaticTaxCalculation) {
            formContext.setValue(TAX, Math.round(tax * 100) / 100, {
                shouldValidate: true,
            });
        }

        const totalWithTax = productsTotal + tax;

        formContext.setValue(
            TOTAL_WITH_TAX,
            Math.round(totalWithTax * 100) / 100,
            {
                shouldValidate: true,
            },
        );
    }
}

const receipt_type_id_FIELD_NAME: keyof IOrderSale = 'receipt_type_id';

export function useCalculateProductsTotal() {
    const formContext = useFormContext<IOrderSale>();
    const { data: receiptTypesData } =
        useOrderSaleOrderSaleReceiptTypeInputQuery();

    const calculateProductsTotal = function (
        params: {
            index?: number;
            automaticTaxCalculation?: boolean;
        } = {},
    ) {
        const index =
            params && params.index !== undefined ? params.index : undefined;

        const orderSaleProducts = formContext.getValues(
            ORDER_SALE_PRODUCTS_FIELD_NAME,
        ) as IOrderSale['order_sale_products'];
        const orderSaleReceiptTypeId = formContext.getValues(
            receipt_type_id_FIELD_NAME,
        ) as IOrderSale['receipt_type_id'];
        const automaticTaxCalculation =
            params.automaticTaxCalculation ??
            (formContext.getValues(
                AUTOMATIC_TAX_CALCULATION,
            ) as IOrderSale['automatic_tax_calculation']);

        const receiptTypes = receiptTypesData?.getReceiptTypes ?? [];
        const selectedReceiptType = receiptTypes.find(
            (rt) => rt.id === orderSaleReceiptTypeId,
        );
        const taxMultiplier = selectedReceiptType?.tax_multiplier ?? 1;

        setProductsTotal({
            orderSaleProducts,
            taxMultiplier,
            automaticTaxCalculation,
            formContext,
            index,
        });
    };

    return {
        calculateProductsTotal: calculateProductsTotal,
    };
}
